import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI, inventoryAPI } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  DollarSign,
  Loader2,
  PackageCheck,
  Percent,
  ReceiptText,
  RefreshCw,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Truck,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: "LKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-LK").format(Number(value) || 0);

const getRequestError = (result, fallback) => {
  const reason = result?.reason;
  return (
    reason?.response?.data?.detail ||
    reason?.response?.data?.message ||
    reason?.message ||
    fallback
  );
};

const getDefaultDashboardPeriod = () => {
  const today = new Date();
  const localDate = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"), String(today.getDate()).padStart(2, "0")].join("-");
  const year = today.getFullYear();
  const financialYearStart = today.getMonth() >= 3 ? year : year - 1;
  return {
    start_date: `${financialYearStart}-04-01`,
    end_date: localDate,
  };
};

const FinancialCard = ({
  title,
  value,
  icon: Icon,
  tone = "slate",
  subtitle,
}) => {
  const tones = {
    revenue: "bg-emerald-50 text-emerald-700 border-emerald-100",
    expense: "bg-rose-50 text-rose-700 border-rose-100",
    profit: "bg-slate-950 text-[#39FF14] border-slate-950",
    margin: "bg-lime-50 text-lime-800 border-lime-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <Card className="overflow-hidden border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              {title}
            </p>
            <p className="mt-2 truncate text-2xl font-bold tracking-[-0.035em] text-slate-950">
              {value}
            </p>
            {subtitle && (
              <p className="mt-2 text-xs text-slate-500">{subtitle}</p>
            )}
          </div>
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${tones[tone]}`}
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ORDER_STAGES = [
  {
    key: "pending",
    label: "Pending",
    icon: Clock3,
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  {
    key: "processing",
    label: "Processing",
    icon: ShoppingCart,
    className: "border-blue-200 bg-blue-50 text-blue-800",
  },
  {
    key: "packed",
    label: "Packed",
    icon: PackageCheck,
    className: "border-violet-200 bg-violet-50 text-violet-800",
  },
  {
    key: "shipped",
    label: "Shipped",
    icon: Truck,
    className: "border-cyan-200 bg-cyan-50 text-cyan-800",
  },
  {
    key: "delivered",
    label: "Delivered",
    icon: CheckCircle2,
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
];

const InsightCard = ({
  title,
  value,
  detail,
  icon: Icon,
  onClick,
  warning = false,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all ${
      warning
        ? "border-amber-200 bg-amber-50 hover:border-amber-300"
        : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
    }`}
  >
    <div
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${warning ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}
    >
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
        {title}
      </p>
      <p className="mt-1 text-xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 truncate text-xs text-slate-500">{detail}</p>
    </div>
    {onClick && <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />}
  </button>
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [salesChart, setSalesChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [secondaryErrors, setSecondaryErrors] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dateRange, setDateRange] = useState(() => getDefaultDashboardPeriod());
  const [appliedDateRange, setAppliedDateRange] = useState(() => getDefaultDashboardPeriod());

  const fetchData = async (range = appliedDateRange) => {
    setDashboardError("");
    setSecondaryErrors([]);

    const [summaryResult, chartResult, topResult, lowStockResult] =
      await Promise.allSettled([
        dashboardAPI.getSummary(range),
        dashboardAPI.getSalesChart(range),
        dashboardAPI.getTopProducts({ ...range, limit: 5 }),
        inventoryAPI.getLowStock(),
      ]);

    const partialErrors = [];

    if (summaryResult.status === "fulfilled") {
      setSummary(summaryResult.value.data);
      setLastUpdated(new Date());
    } else {
      console.error("Failed to fetch dashboard summary:", summaryResult.reason);
      setDashboardError(
        getRequestError(
          summaryResult,
          "The financial dashboard summary could not be loaded.",
        ),
      );
    }

    if (chartResult.status === "fulfilled") {
      setSalesChart(
        Array.isArray(chartResult.value.data) ? chartResult.value.data : [],
      );
    } else {
      console.error("Failed to fetch dashboard chart:", chartResult.reason);
      partialErrors.push("Revenue chart");
    }

    if (topResult.status === "fulfilled") {
      setTopProducts(
        Array.isArray(topResult.value.data) ? topResult.value.data : [],
      );
    } else {
      console.error("Failed to fetch top products:", topResult.reason);
      partialErrors.push("Top products");
    }

    if (lowStockResult.status === "fulfilled") {
      setLowStock(
        Array.isArray(lowStockResult.value.data)
          ? lowStockResult.value.data
          : [],
      );
    } else {
      console.error(
        "Failed to fetch low-stock products:",
        lowStockResult.reason,
      );
      partialErrors.push("Low-stock list");
    }

    setSecondaryErrors(partialErrors);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      setDashboardError("Please select both a start date and an end date.");
      return;
    }
    if (dateRange.start_date > dateRange.end_date) {
      setDashboardError("Start date cannot be after the end date.");
      return;
    }
    setAppliedDateRange(dateRange);
    setRefreshing(true);
    fetchData(dateRange);
  };

  const resetDateRange = () => {
    const nextRange = getDefaultDashboardPeriod();
    setDateRange(nextRange);
    setAppliedDateRange(nextRange);
    setRefreshing(true);
    fetchData(nextRange);
  };

  if (loading) {
    return (
      <div
        className="flex h-96 items-center justify-center"
        data-testid="dashboard-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
      </div>
    );
  }

  // Never display made-up zero values when the required summary request has
  // failed. This was previously misleading because Promise.all discarded the
  // successful requests whenever any one dashboard widget returned an error.
  if (!summary) {
    return (
      <div
        className="mx-auto flex min-h-[26rem] max-w-2xl items-center justify-center"
        data-testid="dashboard-error"
      >
        <Card className="w-full border-rose-200 bg-white shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-700">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h2 className="mt-4 text-xl font-bold text-slate-950">
              Dashboard data could not be loaded
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600">
              {dashboardError ||
                "The backend did not return the required financial summary."}
            </p>
            <Button
              type="button"
              onClick={() => {
                setRefreshing(true);
                fetchData();
              }}
              disabled={refreshing}
              className="mt-6 gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Retry dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCounts = summary?.order_status_counts || {};
  const netProfit = Number(summary?.net_profit || 0);
  const netMargin = Number(summary?.net_margin || 0);

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Live business control
          </p>
          <h2 className="mt-1 text-3xl font-bold tracking-[-0.04em] text-slate-950">
            Business Overview
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Revenue, expenses, fulfilment, and stock requiring attention.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-4 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs text-slate-500 md:flex">
            <span>
              <strong className="text-slate-900">
                {formatNumber(summary?.customers_count)}
              </strong>{" "}
              customers
            </span>
            <span className="h-4 w-px bg-slate-200" />
            <span>
              <strong className="text-slate-900">
                {formatNumber(summary?.products_count)}
              </strong>{" "}
              products
            </span>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setRefreshing(true);
              fetchData();
            }}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Dashboard period
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Financial totals, orders, chart, and top products use this inclusive date range.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold text-slate-600">
            From
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(event) => setDateRange((current) => ({ ...current, start_date: event.target.value }))}
              className="mt-1 block h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900"
            />
          </label>
          <label className="text-xs font-semibold text-slate-600">
            To
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(event) => setDateRange((current) => ({ ...current, end_date: event.target.value }))}
              className="mt-1 block h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900"
            />
          </label>
          <Button type="button" onClick={applyDateRange} disabled={refreshing}>
            Apply
          </Button>
          <Button type="button" variant="ghost" onClick={resetDateRange} disabled={refreshing}>
            Current FY
          </Button>
        </div>
      </div>

      {(dashboardError || secondaryErrors.length > 0) && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">
              Some dashboard information could not be refreshed.
            </p>
            <p className="mt-0.5 text-xs text-amber-800">
              {dashboardError ||
                `${secondaryErrors.join(", ")} unavailable. The remaining live figures are still shown.`}
              {lastUpdated &&
                ` Last successful summary: ${lastUpdated.toLocaleTimeString()}.`}
            </p>
          </div>
        </div>
      )}

      <section aria-labelledby="financial-performance-heading">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <h3
              id="financial-performance-heading"
              className="text-lg font-semibold text-slate-950"
            >
              Financial performance
            </h3>
            <p className="text-xs text-slate-500">
              Net profit = total revenue − total expenses · {summary?.period?.start_date} to {summary?.period?.end_date}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <FinancialCard
            title="Total Revenue"
            value={formatCurrency(summary?.total_revenue)}
            icon={TrendingUp}
            tone="revenue"
            subtitle={`${formatCurrency(summary?.sales_revenue)} sales + ${formatCurrency(summary?.other_revenue)} other income`}
          />
          <FinancialCard
            title="Total Expenses"
            value={formatCurrency(summary?.total_expenses)}
            icon={ReceiptText}
            tone="expense"
            subtitle="All expense-account postings, including Quick Transactions"
          />
          <FinancialCard
            title="Net Profit"
            value={formatCurrency(netProfit)}
            icon={DollarSign}
            tone="profit"
            subtitle={
              netProfit >= 0
                ? "Revenue exceeds expenses"
                : "Expenses exceed revenue"
            }
          />
          <FinancialCard
            title="Net Margin"
            value={`${netMargin.toFixed(1)}%`}
            icon={Percent}
            tone="margin"
            subtitle="Profit as a share of revenue"
          />
        </div>
      </section>

      <section aria-labelledby="order-pipeline-heading">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3
              id="order-pipeline-heading"
              className="text-lg font-semibold text-slate-950"
            >
              Order fulfilment pipeline
            </h3>
            <p className="text-xs text-slate-500">
              Each valid order appears in one current stage.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/sales-orders")}
            className="gap-1 text-slate-600"
          >
            View all <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {ORDER_STAGES.map((stage) => {
            const Icon = stage.icon;
            return (
              <button
                key={stage.key}
                type="button"
                onClick={() => navigate(`/sales-orders?stage=${stage.key}`)}
                className={`group rounded-xl border p-4 text-left transition-transform hover:-translate-y-0.5 ${stage.className}`}
                data-testid={`order-stage-${stage.key}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-5 w-5 opacity-75" />
                  <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-60" />
                </div>
                <p className="mt-5 text-3xl font-bold tracking-[-0.05em]">
                  {formatNumber(statusCounts[stage.key])}
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] opacity-75">
                  {stage.label}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="operational-insights-heading">
        <div className="mb-3">
          <h3
            id="operational-insights-heading"
            className="text-lg font-semibold text-slate-950"
          >
            Operational priorities
          </h3>
          <p className="text-xs text-slate-500">
            High-value information requiring regular review.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <InsightCard
            title="Active Orders"
            value={formatNumber(summary?.active_orders)}
            detail="Pending through shipped"
            icon={ShoppingCart}
            onClick={() => navigate("/sales-orders")}
          />
          <InsightCard
            title="Average Order Value"
            value={formatCurrency(summary?.average_order_value)}
            detail="Across revenue-recognized orders"
            icon={CircleDollarSign}
          />
          <InsightCard
            title="Low Stock"
            value={formatNumber(summary?.low_stock_count)}
            detail="Products at or below threshold"
            icon={AlertTriangle}
            warning={Number(summary?.low_stock_count || 0) > 0}
            onClick={() => navigate("/inventory")}
          />
          <InsightCard
            title="Refunds to Process"
            value={formatNumber(summary?.refund_pending_orders)}
            detail="Manual PayHere refunds pending"
            icon={TrendingDown}
            warning={Number(summary?.refund_pending_orders || 0) > 0}
            onClick={() => navigate("/sales-orders")}
          />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <InsightCard
            title="Receivables"
            value={formatCurrency(summary?.receivables)}
            detail="Outstanding customer balances"
            icon={TrendingUp}
            onClick={() => navigate("/accounting")}
          />
          <InsightCard
            title="Payables"
            value={formatCurrency(summary?.payables)}
            detail="Outstanding supplier balances"
            icon={TrendingDown}
            onClick={() => navigate("/accounting")}
          />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 p-5" data-testid="sales-chart-card">
          <CardHeader className="p-0 pb-5">
            <CardTitle className="text-base font-semibold">
              Revenue trend · {summary?.period?.start_date} to {summary?.period?.end_date}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={salesChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={12}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={12}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    stroke="#0f172a"
                    strokeWidth={2.5}
                    dot={{ fill: "#39FF14", stroke: "#0f172a", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 p-5" data-testid="top-products-card">
          <CardHeader className="p-0 pb-5">
            <CardTitle className="text-base font-semibold">
              Top products by revenue
            </CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              Ranked within the selected dashboard period.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    stroke="#64748b"
                    fontSize={12}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                  />
                  <YAxis
                    type="category"
                    dataKey="product_name"
                    stroke="#64748b"
                    fontSize={12}
                    width={120}
                    tickFormatter={(value) =>
                      value.length > 16 ? `${value.substring(0, 16)}…` : value
                    }
                  />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar
                    dataKey="total_revenue"
                    fill="#0f172a"
                    radius={[0, 5, 5, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {lowStock.length > 0 && (
        <Card
          className="border-amber-200 bg-amber-50/60"
          data-testid="low-stock-alert"
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-700" />
                <CardTitle className="text-base font-semibold text-amber-900">
                  Low-stock products
                </CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/inventory")}
                className="text-amber-800"
              >
                Inventory <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {lowStock.slice(0, 6).map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between rounded-lg border border-amber-100 bg-white p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {product.name}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {product.sku}
                    </p>
                  </div>
                  <div className="ml-3 text-right">
                    <p className="text-lg font-bold text-amber-700">
                      {formatNumber(product.stock_quantity)}
                    </p>
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">
                      available
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
